/**
 * GSAP animation helpers for the composition editor.
 * Isolated scope — does not pollute global gsap defaults.
 */
import gsap from "gsap"

export const RM = window.matchMedia("(prefers-reduced-motion: reduce)").matches
export const D = (x: number): number => (RM ? 0 : x)

interface AnimateInOpts {
  y?: number
  x?: number
  scale?: number
  duration?: number
  delay?: number
  ease?: string
}

export function animateIn(el: Element, opts: AnimateInOpts = {}): void {
  const { y = 10, scale = 1, duration = 0.28, delay = 0, ease = "expo.out" } = opts
  gsap.fromTo(el,
    { autoAlpha: 0, y, scale: scale < 1 ? scale : 1 },
    { autoAlpha: 1, y: 0, scale: 1, duration: D(duration), delay: D(delay), ease },
  )
}

interface AnimateOutOpts {
  y?: number
  duration?: number
  ease?: string
  onComplete?: () => void
}

export function animateOut(el: Element, opts: AnimateOutOpts = {}): void {
  const { y = 6, duration = 0.18, ease = "power2.in", onComplete } = opts
  gsap.to(el, { autoAlpha: 0, y, duration: D(duration), ease, onComplete })
}

/**
 * FLIP animation: capture element positions before a DOM change,
 * then animate from old to new positions after the change.
 */
export function capturePositions(selector: string): Record<string, number> {
  const map: Record<string, number> = {}
  document.querySelectorAll(selector).forEach(el => {
    const id = (el as HTMLElement).dataset.id
    if (id) map[id] = el.getBoundingClientRect().left
  })
  return map
}

export function playFlip(beforeMap: Record<string, number>, selector: string, skipId: string | null = null): void {
  document.querySelectorAll(selector).forEach(el => {
    const id = (el as HTMLElement).dataset.id
    if (id === skipId || !(id && id in beforeMap)) {
      if (id && !(id in beforeMap) && id !== skipId) {
        gsap.from(el, { autoAlpha: 0, scale: 0.94, duration: D(0.32), ease: "expo.out" })
      }
      return
    }
    const dx = beforeMap[id] - el.getBoundingClientRect().left
    if (Math.abs(dx) > 1) {
      gsap.from(el, { x: dx, duration: D(0.38), ease: "power3.out" })
    }
  })
}

interface QuickToOpts {
  duration?: number
  ease?: string
}

/**
 * Quick-position helper (compositor-only, uses transform).
 */
export function quickTo(el: Element, prop: string, opts: QuickToOpts = {}): gsap.QuickToFunc {
  return gsap.quickTo(el, prop, { duration: D(opts.duration || 0.12), ease: opts.ease || "power3.out" })
}

export { gsap }
